import { useState } from 'react';
import { ResultsItem, ResultSubItem, IconContent, AccordionToggle, PriorityIndicator, PriorityIndicatorGroup } from '../../../features/Report';
import { Tooltip, Table, CopyButton } from '../../../components';

const formatKiB = (bytes) => {
  const n = Number(bytes) || 0;
  return (n / 1024).toFixed(2);
};

export const PerformanceSection = ({ id, data }) => {
  const [assetMinificationExpanded, setAssetMinificationExpanded] = useState(false);

  const defaultCriteria = {
    status: 'info',
    iconType: 'info',
    importance: { level: 0, text: '' },
    solvability: { level: 0, text: '' },
    isEmpty: true,
    message: 'Data is unavailable.',
    data: null,
  };

  const resourcesMinification = data?.resourcesMinification || defaultCriteria;
  const errorCount = data?.errorCount || 0;
  const toImproveCount = data?.toImproveCount || 0;

  const rows = resourcesMinification?.data?.rows || [];
  const totals = resourcesMinification?.data?.totals || { totalSaved: 0, totalPercent: 0 };

  const assetMinificationTableData = rows?.length > 0
    ? [
        ['URL', 'Reduction Size'],
        ...rows.map((r) => [
          r?.url || '',
          `${formatKiB(r?.bytesSaved)} KiB (${Number(r?.percentSaved || 0).toFixed(2)}%)`,
        ]),
      ]
    : [['URL', 'Reduction Size']];

  return (
    <ResultsItem 
      id={id}
      title="Performance" 
      tooltip="People reward speed, and so do search engines. A site that loads in two seconds or less performs better when it comes to conversion rates, as well as search engine results. With our SEO checker, you can learn how to improve your website's speed and get all the benefits of a fast site."
      errorCount={errorCount}
      toImproveCount={toImproveCount}
    >
      <ResultSubItem
        title="Asset Minification"
        tooltip="Your asset minification describes how well you've compressed (or minified) the resources on your site, like images, that influence your website's speed."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={resourcesMinification?.iconType || 'info'} 
                showTooltip={false} 
              />
            }
          >
            {resourcesMinification?.isEmpty && (
              <p className="text-primary">
                {resourcesMinification?.message || 'Perfect, all your assets are minified.'}
              </p>
            )}
            
            {!resourcesMinification?.isEmpty && (
              <div className="table-content-wrapper">
                <p className="text-primary">
                  You could reduce {Number(totals?.totalPercent || 0).toFixed(2)}% ({formatKiB(totals?.totalSaved)} KiB) by minifying the asset(s) listed below.
                </p>
                <Table
                  data={assetMinificationTableData}
                  className="table-asset-minification"
                  id="asset-minification-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#asset-minification-table" />
                </div>
              </div>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={assetMinificationExpanded}
            onClick={() => setAssetMinificationExpanded(!assetMinificationExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={assetMinificationExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Asset Minification Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Minify your HTML, CSS, and JavaScript</li>
                <li>Compress HTML, CSS, JavaScript, and image files</li>
                <li>Use a content distribution network (CDN)</li>
              </ul>
            </IconContent>
          }
          sideContent={
            <PriorityIndicatorGroup>
              <PriorityIndicator text="Medium Impact" level={2} />
              <PriorityIndicator text="Hard to Solve" level={3} />
            </PriorityIndicatorGroup>
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/blog/marketing/what-is-page-speed/" target="_blank" rel="noopener noreferrer">
                    What Is Page Speed & How to Improve Yours
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/blog/marketing/speed-site-speed-performance-checklist/" target="_blank" rel="noopener noreferrer">
                    13-Point Site Speed Checklist
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="https://www.webfx.com/blog/marketing/what-is-page-speed/" target="_blank" rel="noopener noreferrer">
                    Is Page Speed a Ranking Factor for SEO?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>
    </ResultsItem>
  );
};
